import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { listAlerts, ackAlert, listGroups } from "@/lib/api";
import { Bell, CheckCircle2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChannelLink } from "@/components/ChannelLink";

const sevBadge = {
    critical: "text-rose-300 bg-rose-500/10 border-rose-500/20",
    warning: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    info: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
};

const AlertList = ({ onlyUnacked }) => {
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState({});
    const load = async () => {
        try {
            const data = await listAlerts(onlyUnacked);
            setItems(data.alerts || []);
        } catch {}
    };
    useEffect(() => {
        listGroups().then((gs) => {
            const list = Array.isArray(gs) ? gs : (gs?.groups || []);
            const m = {};
            list.forEach((g) => { m[g.chat_id] = { username: g.username, title: g.title }; });
            setGroups(m);
        }).catch(() => {});
    }, []);
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlyUnacked]);

    const onAck = async (id) => {
        try {
            await ackAlert(id);
            toast.success("تم التأكيد");
            load();
        } catch {
            toast.error("فشل");
        }
    };

    if (items.length === 0) {
        return (
            <Card className="bg-card/40">
                <CardContent className="p-12 text-center">
                    <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/40">
                        <Bell className="h-7 w-7 text-muted-foreground" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold">لا توجد تنبيهات</h3>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-2">
            {items.map((a) => (
                <Card key={a.id} className="bg-card/60 backdrop-blur" data-testid="alert-row">
                    <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className={sevBadge[a.severity] || ""}>
                                    {a.severity}
                                </Badge>
                                <span className="font-medium">{a.title}</span>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">{a.message}</p>
                            {a.chat_title && (
                                <p className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
                                    في: <ChannelLink title={a.chat_title} username={groups[a.chat_id]?.username} chatId={a.chat_id} showUsername={false} />
                                </p>
                            )}
                        </div>
                        {!a.acknowledged ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onAck(a.id)}
                                className="gap-1"
                                data-testid="alert-acknowledge-button"
                            >
                                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                                تأكيد
                            </Button>
                        ) : (
                            <Badge variant="outline" className="text-emerald-300 bg-emerald-500/10">
                                مؤكّد
                            </Badge>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

export default function Alerts() {
    return (
        <div className="space-y-4 md:space-y-6">
            <div>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">التنبيهات</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    تنبيهات فورية للأحداث الحرجة
                </p>
            </div>
            <Tabs defaultValue="unacked" data-testid="alerts-tabs">
                <TabsList>
                    <TabsTrigger value="unacked" data-testid="alerts-tab-unacked">غير مؤكدة</TabsTrigger>
                    <TabsTrigger value="all" data-testid="alerts-tab-all">الكل</TabsTrigger>
                </TabsList>
                <TabsContent value="unacked">
                    <AlertList onlyUnacked />
                </TabsContent>
                <TabsContent value="all">
                    <AlertList onlyUnacked={false} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
